import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';

const AMOUNT_OPTIONS = ['1g', '2g', '3.5g', '7g', '14g'];

export default function AdminOrderPanel() {
  const showToast = useToast();
  const [users, setUsers] = useState([]);
  const [strains, setStrains] = useState([]);
  const [form, setForm] = useState({ user_id: '', strain_id: '', amount: '', notes: '', order_date: '', order_time: '', custom_price: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/users?sort=username&order=asc&limit=500').then(data => setUsers(data.rows || [])).catch(() => {});
    api.get('/admin/strains/all').then(setStrains).catch(() => {});
  }, []);

  const inStockStrains = strains.filter(s => s.is_in_stock);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.custom_price === '') delete payload.custom_price;
      else payload.custom_price = parseFloat(payload.custom_price);
      await api.post('/bookings/admin-place', payload);
      const user = users.find(u => u.id === form.user_id);
      const strain = strains.find(s => s.id === form.strain_id);
      showToast('success', `Order placed for ${user?.username || 'user'}: ${form.amount} ${strain?.name || ''}`);
      setForm(f => ({ ...f, strain_id: '', amount: '', notes: '', custom_price: '' }));
      api.get('/admin/strains/all').then(setStrains).catch(() => {});
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>Place Order for User</h3>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        Create a pending order on behalf of a member. Supports backdating.
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>User</label>
            <select
              value={form.user_id}
              onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
              required
            >
              <option value="">Select user...</option>
              {users.filter(u => !u.is_admin).map(u => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Strain</label>
            <select
              value={form.strain_id}
              onChange={e => setForm(f => ({ ...f, strain_id: e.target.value }))}
              required
            >
              <option value="">Select strain...</option>
              {inStockStrains.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({parseFloat(s.stock_grams)}g)</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Amount</label>
            <select
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              required
            >
              <option value="">Weight...</option>
              {AMOUNT_OPTIONS.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Custom Price £ (optional)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 20.00"
              value={form.custom_price}
              onChange={e => setForm(f => ({ ...f, custom_price: e.target.value }))}
              style={{ fontSize: 12 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Date (optional)</label>
            <input
              type="date"
              value={form.order_date}
              onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))}
              style={{ fontSize: 12 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Time (optional)</label>
            <input
              type="time"
              value={form.order_time}
              onChange={e => setForm(f => ({ ...f, order_time: e.target.value }))}
              style={{ fontSize: 12 }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Notes (optional)</label>
          <input
            placeholder="e.g. Legacy order, customer request..."
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          />
        </div>

        {form.custom_price !== '' && (
          <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 6 }}>
            Discounted price — no points or spins will be awarded for this order
          </div>
        )}
        {form.order_date && (
          <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>
            Backdated order — will appear as {new Date(form.order_date + 'T' + (form.order_time || '12:00')).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        )}

        <button type="submit" className="btn-primary btn-small" disabled={saving} style={{ width: '100%' }}>
          {saving ? 'Placing order...' : 'Place Order'}
        </button>
      </form>
    </div>
  );
}
